import PropTypes from 'prop-types';
import { MapPin, Star, Wifi, Car, Coffee } from 'lucide-react';
import { Heading, Text } from '@/components/ui/index.js';
import styles from './VenueCard.module.css';

const sportTypeIcons = {
  'Multi-sport': '🏆',
  'Basketball': '🏀',
  'Soccer': '⚽',
  'Tennis': '🎾',
  'Swimming': '🏊',
  'Running': '🏃',
  'Cycling': '🚴',
  'Yoga': '🧘',
};

/**
 * VenueCard - Displays venue information with glassmorphism styling
 * 
 * @param {Object} venue - Venue data
 * @param {function} onClick - Click handler
 * @param {Object} style - Additional inline styles
 */
export function VenueCard({ venue, onClick, style }) {
  const amenityIcons = [
    { icon: Wifi, label: 'WiFi' },
    { icon: Car, label: 'Parking' },
    { icon: Coffee, label: 'Cafe' },
  ];

  return (
    <article 
      className={styles.card}
      onClick={onClick}
      style={style}
    >
      {/* Image Placeholder */}
      <div className={styles.imageContainer}>
        <div className={styles.imagePlaceholder}>
          <span className={styles.sportEmoji}>
            {sportTypeIcons[venue.sport] || '🏆'}
          </span>
        </div>
        <div className={styles.rating}>
          <Star size={14} fill="currentColor" className={styles.starIcon} />
          <Text as="span" size="sm" weight="semibold" color="primary">
            {venue.rating}
          </Text>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        <Heading level={3} size="sm" color="primary" className={styles.name}>
          {venue.name}
        </Heading>

        <div className={styles.meta}>
          <Text size="xs" color="secondary" className={styles.sportType}>
            {venue.sport}
          </Text>
          <span className={styles.separator}>•</span>
          <div className={styles.distance}>
            <MapPin size={12} className={styles.distanceIcon} />
            <Text size="xs" color="muted">
              {venue.distance}
            </Text>
          </div>
        </div>

        {/* Amenities */}
        <div className={styles.amenities}>
          {amenityIcons.map(({ icon: Icon, label }) => (
            <div key={label} className={styles.amenity} title={label}>
              <Icon size={14} className={styles.amenityIcon} />
            </div>
          ))}
        </div>

        {/* View Button */}
        <button 
          className={styles.viewButton}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        >
          View
        </button>
      </div>
    </article>
  );
}

VenueCard.propTypes = {
  venue: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    sport: PropTypes.string.isRequired,
    distance: PropTypes.string.isRequired,
    rating: PropTypes.number.isRequired,
  }).isRequired,
  onClick: PropTypes.func,
  style: PropTypes.object,
};

export default VenueCard;
